import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useGetAppointmentDetailQuery, useUpdateAppointmentStatusMutation } from "../../../store/api/member";
import PageView from "../../../components/commonComponents/Viewpage";

const AppointmentView = () => {
  const { id } = useParams();

  const { data, isLoading } = useGetAppointmentDetailQuery(id);
  const [updateStatus, { isLoading: updating }] = useUpdateAppointmentStatusMutation();

  const [status, setStatus] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (data?.data) {
      setStatus(data.data.status);
    }
  }, [data]);

  const handleStatusUpdate = async () => {
    const payload = { status };
    if (status === "rejected") {
      payload.reason = reason;
    }

    try {
      await updateStatus({ appointment_id: id, data: payload }).unwrap();
      alert("Status Updated Successfully!");
    } catch (err) {
      alert("Error updating status");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      approved: { color: "bg-green-100 text-green-800 border-green-200", label: "Approved" },
      rejected: { color: "bg-red-100 text-red-800 border-red-200", label: "Rejected" },
      pending: { color: "bg-yellow-100 text-yellow-800 border-yellow-200", label: "Pending" }
    };
    
    const config = statusConfig[status] || { color: "bg-gray-100 text-gray-800 border-gray-200", label: status };
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (isLoading) return (
    <PageView title="Appointment Details" closeTo="/appointments">
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading appointment details...</p>
        </div>
      </div>
    </PageView>
  );

  const item = data?.data;

  return (
    <PageView title="Appointment Details" closeTo="/appointments">
      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Appointment Information */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Appointment Information</h2>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="font-medium text-gray-700">User Name</span>
                <span className="text-gray-900">{item.user_name}</span>
              </div>
              
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="font-medium text-gray-700">Gym</span>
                <span className="text-gray-900">{item.gym_name}</span>
              </div>
              
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="font-medium text-gray-700">Start Date</span>
                <span className="text-gray-900">{item.start_date}</span>
              </div>
              
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="font-medium text-gray-700">End Date</span>
                <span className={item.end_date ? "text-gray-900" : "text-gray-500"}>
                  {item.end_date || "Not set"}
                </span>
              </div>
              
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="font-medium text-gray-700">Appointment Time</span>
                <span className="text-gray-900">{item.appointment_time}</span>
              </div>
              
              <div className="flex justify-between items-center py-3">
                <span className="font-medium text-gray-700">Current Status</span>
                {getStatusBadge(item.status)}
              </div>
            </div>
          </div>

          {/* Right Column - Status Update */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Update Status</h3>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select 
                  value={status} 
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              {status === "rejected" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rejection Reason
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Please provide a reason for rejection..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none transition-colors duration-200"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This reason will be shared with the user
                  </p>
                </div>
              )}

              <button 
                disabled={updating} 
                onClick={handleStatusUpdate}
                className={`w-full py-3 px-4 rounded-lg font-medium text-white transition-colors duration-200 ${
                  updating 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                }`}
              >
                {updating ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Updating Status...
                  </div>
                ) : (
                  "Update Status"
                )}
              </button>

              {/* Status Help Text */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-800 mb-2">Status Information</h4>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• <strong>Pending:</strong> Appointment is awaiting approval</li>
                  <li>• <strong>Approved:</strong> Appointment is confirmed</li>
                  <li>• <strong>Rejected:</strong> Appointment is declined (reason required)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Current Status Display */}
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Current Status</h3>
              <p className="text-gray-600 mt-1">This shows the current status of the appointment</p>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500 mb-1">Current Status</div>
              {getStatusBadge(item.status)}
            </div>
          </div>
        </div>
      </div>
    </PageView>
  );
};

export default AppointmentView;